// JOBE-0106 follow-up: SubProjectsBlock — side-rail block #1 (was
// "Dependents" in the spec; renamed for honesty about v1 limitations).
//
// What the spec wanted: projects that have this project as a `partOf`
// ancestor (sub-projects in the project tree). That requires
// `inboundRelations.partOf=<projectURN>` on `/api/entities`, which is
// NOT in the BFF allowlist today (`listEntitiesAllowedParams` in
// `src/web/bff/entities_list.go` accepts only the type / owner / by /
// inProject / inCollection / since / until / q / sort / limit / cursor
// set). Adding it is small substrate work but out of scope for this
// side-rail follow-up PR.
//
// v1 stance: ships with a Preview pill and the closest allowlisted
// proxy query (`type=project&inProject=<urn>` — projects anchored to
// this one via the containment edge). Writers don't emit project →
// project `inProject` edges today, so this returns empty in practice.
// The Preview pill signals "feature pending substrate" — same shape
// as ProvenanceBlock's stub posture.
//
// Substrate follow-up (separate JOBE-NNNN): widen the BFF allowlist to
// accept `inboundRelations.partOf` (and likely sibling predicates as
// the surface generalises). Once it ships, this block re-queries with
// the proper filter and the Preview pill comes off.
//
// Cedar posture: silent-drop. Any failure (400 allowlist / 500 /
// Cedar deny re-shaped 200 empty) renders the empty-state card.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const TOP_N = 3;

export function DependentsBlock({ projectURN }) {
  const [state, setState] = useState({ loadState: 'loading', items: [], total: 0 });

  useEffect(() => {
    if (!projectURN) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    setState({ loadState: 'loading', items: [], total: 0 });

    const url = `/api/entities?type=project&inProject=${encodeURIComponent(projectURN)}&limit=50`;
    fetch(url, { credentials: 'same-origin', signal: ctrl.signal })
      .then(async (res) => {
        if (!alive) return;
        if (!res.ok) {
          // Silent-drop: a 400 (allowlist) / 500 / cedar-deny falls
          // through to an empty list. No banner; the empty-state card
          // covers the surface.
          setState({ loadState: 'ready', items: [], total: 0 });
          return;
        }
        const body = await res.json();
        if (!alive) return;
        const items = Array.isArray(body?.items) ? body.items : [];
        setState({
          loadState: 'ready',
          items: items.slice(0, TOP_N),
          total: items.length,
        });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({ loadState: 'ready', items: [], total: 0 });
      });

    return () => { alive = false; ctrl.abort(); };
  }, [projectURN]);

  return (
    <section
      className="project-home-rail-block project-home-rail-dependents card"
      data-testid="rail-dependents"
      aria-label="Sub-projects"
    >
      <header className="project-home-rail-block-header">
        <h3 className="project-home-rail-block-title">
          Sub-projects{' '}
          <span className="lane-preview-marker" aria-label="preview feature">
            Preview
          </span>
        </h3>
        <span className="project-home-rail-block-count mono" data-testid="rail-dependents-count">
          {state.total}
        </span>
      </header>
      {state.items.length === 0 ? (
        <p className="muted project-home-rail-empty" data-testid="rail-dependents-empty">
          No sub-projects yet.
        </p>
      ) : (
        <ul className="project-home-rail-list">
          {state.items.map((item) => (
            <li key={item.urn} className="project-home-rail-list-row">
              <Link
                to={`/projects/${encodeURIComponent(item.urn)}`}
                className="project-home-rail-list-link"
                data-testid={`rail-dependents-row-${item.urn}`}
              >
                {item.name || item.urn}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
